import React, { useState } from 'react';
const styles = require('./styles.pcss');
import * as _ from 'lodash-es';
import Dialog from '@mui/material/Dialog';
import cx from 'classnames';
import { useForm, SubmitHandler, Controller, get } from 'react-hook-form';
import {
  Button,
  MenuItem,
  Select,
  TextField,
  OutlinedInput,
} from '@mui/material';
import { AIRLINE_NAME_MAP } from '../../helpers/airlinemap';
import AutoSuggest from 'components/Inputs/AutoSuggest/AutoSuggest';
import SvgAirplane from '../../static/icons/svgAirplane';

enum StatusEnum {
  completed = 'completed',
  canceled = 'canceled',
  pending = 'pending',
  errored = 'errored',
}

type Inputs = {
  email: string;
  status: StatusEnum;
  segments?: any;
  confirmationNumber?: string;
  // --- CAR BOOKING FIELDS ---
  basePrice?: number;
  totalPrice?: number;
  requiredCharges?: number;
  providerReturnDate?: string;
  // --- HOTEL BOOKING FIELDS ---
  customerPrice?: number;
  departureDate?: string;
};

type Props = {
  open: boolean;
  close: any;
  type: string;
  booking: any;
  id: any;
  confirmationNumber: string;
  status: string;
  identifier: any;
  additionalEmails: string;
  updateBooking: Function;
  notes?: string;
  // --- CAR BOOKING FIELDS ---
  basePrice?: number;
  totalPrice?: number;
  requiredCharges?: number;
  providerReturnDate?: string;
  // --- HOTEL BOOKING FIELDS ---
  customerPrice?: number;
  departureDate?: string;
};

export default function ModifyBookingsDetailsPopup(props: Props) {
  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm();
  watch();

  let airlines: any = [];
  _.forOwn(AIRLINE_NAME_MAP, function (value, key) {
    airlines.push({ code: key, name: value });
  });

  const segments = props.booking?.segments?.reduce(
    (allSegments: any[], segmentGroup: any) => {
      allSegments.push(...segmentGroup.segment);
      return allSegments;
    },
    [],
  );

  // Resolve flat pricing fields with GDS fallback for bookings that pre-date
  // the flat-field migration (e.g. bookings fetched via PNR lookup whose taxes
  // are only stored in reservations[0].priceInfo.Taxes as "USD100.00").
  const parseGdsCurrency = (raw: any): string => {
    if (raw === null || raw === undefined) return '';
    if (typeof raw === 'number') return String(raw);
    return String(raw).replace(/^[A-Z]{3}/, '');
  };
  const gdsBasePrice = _.get(props.booking, 'reservations[0].priceInfo.BasePrice', '');
  const gdsTaxPrice  = _.get(props.booking, 'reservations[0].priceInfo.Taxes', '');
  const defaultBasePrice  = props.booking?.basePrice  ?? parseGdsCurrency(gdsBasePrice);
  const defaultTaxPrice   = props.booking?.taxPrice   ?? parseGdsCurrency(gdsTaxPrice);

  // totalPrice must equal basePrice + taxPrice for the backend consistency check.
  // Do NOT fall back to customerPrice — for GDS bookings customerPrice includes
  // fees and does not equal basePrice + taxPrice, which would trigger a 422.
  const parsedBase  = parseFloat(String(defaultBasePrice));
  const parsedTax   = parseFloat(String(defaultTaxPrice));
  const defaultTotalPrice = props.booking?.totalPrice != null
    ? props.booking.totalPrice
    : (!isNaN(parsedBase) && !isNaN(parsedTax))
      ? (parsedBase + parsedTax).toFixed(2)
      : '';

  const onClose = () => {
    reset();
    props.close();
  };

  const onSubmit: SubmitHandler<Inputs> = (data: any) => {
    // ── Existing PUT fields (status / confirmation / segments) ─────────────────
    data.segments = data.segments?.map((segment: any) => {
      // Existing booking stores airline as a code ("AA"); new selections use
      // the full name ("American Airlines"). Resolve whichever is present.
      const airlineEntry =
        airlines.find((a: any) => a.name === segment.airline) ??
        airlines.find((a: any) => a.code === segment.airline);
      const airlineCode = airlineEntry?.code ?? segment.airline ?? '';

      const {
        arrivalTime,
        arrivalDate,
        departureDate,
        departureTime,
        arrival: oldArrival,
        departure: oldDeparture,
      } = segment;

      const arrival = `${arrivalDate}T${arrivalTime}${oldArrival?.substring(16)}`;
      const departure = `${departureDate}T${departureTime}${oldDeparture?.substring(16)}`;

      return {
        ...segment,
        arrival,
        departure,
        airline: airlineCode,
      };
    });
    // Build the single PUT payload. Pricing/date fields are now handled by the
    // extended PUT /bookings/details endpoint — no separate PATCH call needed.
    const basePriceVal = parseFloat(data.basePrice);
    const taxPriceVal = parseFloat(data.taxPrice);
    const totalPriceVal = parseFloat(data.totalPrice);

    const updatePayload: Record<string, any> = {
      bookingId: props.id,
      status: data.status,
      confirmationNumber: data.confirmationNumber,
      notes: data.notes,
      segments: data.segments,
      travelerIds: data.travelerIds,
    };

    // Only include flight pricing/date fields when they have valid values.
    if (props.type === 'flight') {
      if (isFinite(basePriceVal)) updatePayload.basePrice = basePriceVal;
      if (isFinite(taxPriceVal)) updatePayload.taxPrice = taxPriceVal;
      if (isFinite(totalPriceVal)) updatePayload.totalPrice = totalPriceVal;
      if (data.reservationStartDate) updatePayload.reservationStartDate = data.reservationStartDate;
      if (data.endDate) updatePayload.endDate = data.endDate;
      if (data.dropOffDate) updatePayload.dropOffDate = data.dropOffDate;
    }

    // Include hotel booking pricing/date fields when they have valid values.
    if (props.type === 'hotel') {
      if (isFinite(basePriceVal)) updatePayload.basePrice = basePriceVal;
      if (isFinite(totalPriceVal)) updatePayload.totalPrice = totalPriceVal;
      const customerPriceVal = parseFloat(data.customerPrice);
      if (isFinite(customerPriceVal)) updatePayload.customerPrice = customerPriceVal;
      if (data.departureDate) updatePayload.departureDate = data.departureDate;
    }

    // Include car booking pricing/date fields when they have valid values.
    if (props.type === 'car') {
      if (isFinite(basePriceVal)) updatePayload.basePrice = basePriceVal;
      if (isFinite(totalPriceVal)) updatePayload.totalPrice = totalPriceVal;
      const requiredChargesVal = parseFloat(data.requiredCharges);
      if (isFinite(requiredChargesVal)) updatePayload.requiredCharges = requiredChargesVal;
      if (data.providerReturnDate) updatePayload.endDate = data.providerReturnDate;
    }


    // Close only after the API confirms success — not optimistically.
    props.updateBooking({ ...updatePayload, onSuccess: props.close });
  };

  return (
    <Dialog
      maxWidth={'md'}
      fullWidth={true}
      className={cx(styles.dialogBox, styles.modifyBookingDetailsPopup)}
      open={props.open}
      onClose={close}
    >
      <div className={styles.contentContainer}>
        <h1 className={styles.headerText}>Modify Booking Details</h1>
        <h2 className={styles.subText}>
          Modify the booking details stored in the TravelBank system
        </h2>
        <form
          className={styles.fieldContainer}
          onSubmit={handleSubmit(onSubmit)}
        >
          <TextField
            label="Confirmation Number"
            defaultValue={props.confirmationNumber}
            variant="outlined"
            error={!!get(errors, 'confirmationNumber')?.message}
            helperText={get(errors, 'confirmationNumber')?.message}
            inputProps={{
              ...register('confirmationNumber'),
            }}
          />

          <Controller
            name={'status'}
            control={control}
            defaultValue={props.status}
            render={({ field }) => (
              <Select
                {...field}
                input={
                  <OutlinedInput
                    name={field.name}
                  />
                }
                variant="outlined"
              >
                <MenuItem
                  key="canceled"
                  value="canceled"
                >
                  Canceled
                </MenuItem>
                <MenuItem
                  key="pending"
                  value="pending"
                >
                  Pending
                </MenuItem>
                <MenuItem
                  key="errored"
                  value="errored"
                >
                  Errored
                </MenuItem>
                <MenuItem
                  key="completed"
                  value="completed"
                >
                  Completed
                </MenuItem>
              </Select>
            )}
          />
          {props.booking?.type === 'car' && (
            <>
              <TextField
                label="Base Price"
                defaultValue={props.basePrice}
                variant="outlined"
                type="number"
                inputProps={{
                  ...register('basePrice', { valueAsNumber: true }),
                }}
              />
              <TextField
                label="Total Price"
                defaultValue={props.totalPrice}
                variant="outlined"
                type="number"
                inputProps={{
                  ...register('totalPrice', { valueAsNumber: true }),
                }}
              />
              <TextField
                label="Required Charges (Taxes & Fees)"
                defaultValue={props.requiredCharges}
                variant="outlined"
                type="number"
                inputProps={{
                  ...register('requiredCharges', { valueAsNumber: true }),
                }}
              />
              <TextField
                label="Provider Return Date (YYYY-MM-DD)"
                placeholder="YYYY-MM-DD"
                defaultValue={props.providerReturnDate?.substring(0, 10)}
                variant="outlined"
                error={!!get(errors, 'providerReturnDate')?.message}
                helperText={get(errors, 'providerReturnDate')?.message}
                inputProps={{
                  ...register('providerReturnDate', {
                    pattern: {
                      value: /^\d{4}-\d{2}-\d{2}$/,
                      message: 'Must be YYYY-MM-DD',
                    },
                  }),
                }}
              />
            </>
          )}

          {props.booking?.type === 'hotel' && (
            <>
              <TextField
                label="Base Price"
                defaultValue={props.basePrice}
                variant="outlined"
                type="number"
                inputProps={{
                  ...register('basePrice', { valueAsNumber: true }),
                }}
              />
              <TextField
                label="Total Price"
                defaultValue={props.totalPrice}
                variant="outlined"
                type="number"
                inputProps={{
                  ...register('totalPrice', { valueAsNumber: true }),
                }}
              />
              <TextField
                label="Customer Price"
                defaultValue={props.customerPrice}
                variant="outlined"
                type="number"
                inputProps={{
                  ...register('customerPrice', { valueAsNumber: true }),
                }}
              />
              <TextField
                label="Departure Date (YYYY-MM-DD)"
                placeholder="YYYY-MM-DD"
                defaultValue={props.departureDate?.substring(0, 10)}
                variant="outlined"
                error={!!get(errors, 'departureDate')?.message}
                helperText={get(errors, 'departureDate')?.message}
                inputProps={{
                  ...register('departureDate', {
                    pattern: {
                      value: /^\d{4}-\d{2}-\d{2}$/,
                      message: 'Must be YYYY-MM-DD',
                    },
                  }),
                }}
              />
            </>
          )}

          {props.booking?.type === 'flight' &&
            segments.map((segment: any, index: number) => {
              return (
                <div
                  className={styles.segmentContainer}
                  key={`segment-fields-${index}`}
                >
                  <h2 className={styles.segmentHeader}>Segment {index + 1}</h2>
                  <div className={styles.segmentFieldContainer}>
                    <Controller
                      name={'segments.' + index + '.airline'}
                      control={control}
                      defaultValue={
                        AIRLINE_NAME_MAP[
                          (segment?.airline as keyof typeof AIRLINE_NAME_MAP) ||
                            (segment?.operatingAirline as keyof typeof AIRLINE_NAME_MAP)
                        ]
                      }
                      render={({ field }) => (
                        <AutoSuggest
                          {...field}
                          label={'Airline'}
                          className={styles.segmentAirlineContainer}
                          dataSet={airlines}
                          maxMenuHeight={90}
                        />
                      )}
                    />
                    <TextField
                      label="Flight Number"
                      defaultValue={segment.flightNumber}
                      variant="outlined"
                      error={
                        !!get(errors, 'segments.' + index + '.flightNumber')
                          ?.message
                      }
                      helperText={
                        get(errors, 'segments.' + index + '.flightNumber')
                          ?.message
                      }
                      inputProps={{
                        ...register('segments.' + index + '.flightNumber', {
                          required: 'This is required',
                        }),
                      }}
                    />
                    <TextField
                      label="Air Plane Name"
                      defaultValue={segment.planeName}
                      variant="outlined"
                      inputProps={{
                        ...register('segments.' + index + '.planeName'),
                      }}
                    />
                  </div>
                  <div className={styles.segmentFieldContainer}>
                    <TextField
                      label="From Airport Code"
                      defaultValue={segment.from}
                      variant="outlined"
                      error={
                        !!get(errors, 'segments.' + index + '.from')?.message
                      }
                      helperText={
                        get(errors, 'segments.' + index + '.from')?.message
                      }
                      inputProps={{
                        ...register('segments.' + index + '.from', {
                          required: 'This field is required',
                          pattern: {
                            message: 'Invalid 3 digit IATA code',
                            value: /^[a-zA-Z0-9]{3}$/,
                          },
                        }),
                      }}
                    />

                    <SvgAirplane
                      width={24}
                      height={20}
                    />
                    <TextField
                      label="To Airport Code"
                      defaultValue={segment.to}
                      variant="outlined"
                      error={
                        !!get(errors, 'segments.' + index + '.to')?.message
                      }
                      helperText={
                        get(errors, 'segments.' + index + '.to')?.message
                      }
                      inputProps={{
                        ...register('segments.' + index + '.to', {
                          required: 'This field is required',
                          pattern: {
                            message: 'Invalid 3 digit IATA code',
                            value: /^[a-zA-Z0-9]{3}$/,
                          },
                        }),
                      }}
                    />
                  </div>
                  <div className={styles.segmentFieldContainer}>
                    <TextField
                      label="Depart Time (hh:mm 24hr)"
                      defaultValue={segment.departure?.substring(11, 16)}
                      variant="outlined"
                      error={
                        !!get(errors, 'segments.' + index + '.departureTime')
                          ?.message
                      }
                      helperText={
                        get(errors, 'segments.' + index + '.departureTime')
                          ?.message
                      }
                      inputProps={{
                        ...register('segments.' + index + '.departureTime', {
                          required: 'This field is required',
                          pattern: {
                            value: /^(?:[01]\d|2[0-3]):[0-5]\d$/,
                            message: 'Invalid hh:mm time value',
                          },
                        }),
                      }}
                    />
                    <TextField
                      label="Arrival Time (hh:mm 24hr)"
                      defaultValue={segment.arrival?.substring(11, 16)}
                      variant="outlined"
                      error={
                        !!get(errors, 'segments.' + index + '.arrivalTime')
                          ?.message
                      }
                      helperText={
                        get(errors, 'segments.' + index + '.arrivalTime')
                          ?.message
                      }
                      inputProps={{
                        ...register('segments.' + index + '.arrivalTime', {
                          required: 'This field is required',
                          pattern: {
                            value: /^(?:[01]\d|2[0-3]):[0-5]\d$/,
                            message: 'Invalid hh:mm time value',
                          },
                        }),
                      }}
                    />
                  </div>
                  <div className={styles.segmentFieldContainer}>
                    <TextField
                      label="Depart Date (YYYY-MM-DD)"
                      placeholder="YYYY-MM-DD"
                      defaultValue={segment.departure?.substring(0, 10)}
                      variant="outlined"
                      error={
                        !!get(errors, 'segments.' + index + '.departureDate')
                          ?.message
                      }
                      helperText={
                        get(errors, 'segments.' + index + '.departureDate')
                          ?.message
                      }
                      inputProps={{
                        ...register('segments.' + index + '.departureDate', {
                          required: 'This field is required',
                          pattern: {
                            value:
                              /^(?:\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[1-2]\d|3[0-1]))$/,
                            message: 'Invalid YYYY-MM-DD value',
                          },
                        }),
                      }}
                    />
                    <TextField
                      label="Arrival Date (YYYY-MM-DD)"
                      placeholder="YYYY-MM-DD"
                      defaultValue={segment.arrival?.substring(0, 10)}
                      variant="outlined"
                      error={
                        !!get(errors, 'segments.' + index + '.arrivalDate')
                          ?.message
                      }
                      helperText={
                        get(errors, 'segments.' + index + '.arrivalDate')
                          ?.message
                      }
                      inputProps={{
                        ...register('segments.' + index + '.arrivalDate', {
                          required: 'This field is required',
                          pattern: {
                            value:
                              /^(?:\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[1-2]\d|3[0-1]))$/,
                            message: 'Invalid YYYY-MM-DD value',
                          },
                        }),
                      }}
                    />
                  </div>
                  <input
                    {...register('segments.' + index + '.departure', {
                      value: segment.departure,
                    })}
                    type="hidden"
                  />
                  <input
                    {...register('segments.' + index + '.arrival', {
                      value: segment.arrival,
                    })}
                    type="hidden"
                  />
                </div>
              );
            })}

          {props.type === 'flight' && (
            <>
              <h2 className={styles.subText}>Pricing &amp; Dates</h2>

              <TextField
                label="Reservation Start Date (YYYY-MM-DD)"
                placeholder="YYYY-MM-DD"
                defaultValue={(props.booking?.arrivalDate ?? '').substring(0, 10)}
                variant="outlined"
                {...register('reservationStartDate')}
              />

              <TextField
                label="End Date (YYYY-MM-DD)"
                placeholder="YYYY-MM-DD"
                defaultValue={(props.booking?.endDate ?? '').substring(0, 10)}
                variant="outlined"
                {...register('endDate')}
              />

              <TextField
                label="Drop-off Date (YYYY-MM-DD)"
                placeholder="YYYY-MM-DD"
                defaultValue={(props.booking?.dropOffDate ?? '').substring(0, 10)}
                variant="outlined"
                {...register('dropOffDate')}
              />

              <TextField
                label="Base Price"
                type="number"
                defaultValue={defaultBasePrice}
                variant="outlined"
                inputProps={{ min: 0, step: '0.01' }}
                {...register('basePrice')}
              />

              <TextField
                label="Tax Price"
                type="number"
                defaultValue={defaultTaxPrice}
                variant="outlined"
                inputProps={{ min: 0, step: '0.01' }}
                {...register('taxPrice')}
              />

              <TextField
                label="Total Price (must equal Base + Tax)"
                type="number"
                defaultValue={defaultTotalPrice}
                variant="outlined"
                inputProps={{ min: 0, step: '0.01' }}
                {...register('totalPrice')}
              />
            </>
          )}

          <div className={styles.actionButtonContainer}>
            <Button
              variant="outlined"
              onClick={() => onClose()}
            >
              Cancel Updates
            </Button>
            <Button
              className={styles.button}
              variant="contained"
              color="primary"
              type="submit"
            >
              Update
            </Button>
          </div>
        </form>
      </div>
    </Dialog>
  );
}
